// Phase 9 — "Resume your session from X ago" banner.
//
// Surfaces on the project chat page when no sessionId is in the URL AND
// the user has a session for this project that was last active within
// 24h. We never auto-resume — surprise context is worse than a click.

import { useState } from "react";

import { useRelativeTime } from "../i18n/format.js";
import { useT } from "../i18n/index.js";
import { Button } from "../ui/Button";

import { useSessions, type SessionIndexEntry } from "./useSessions";

interface ResumeBannerProps {
  readonly projectId: number;
  readonly onResume: (sessionId: string) => void;
}

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

const findResumable = (
  sessions: ReadonlyArray<SessionIndexEntry>,
  now: Date,
): SessionIndexEntry | null => {
  for (const s of sessions) {
    if (s.status === "discarded") continue;
    const ts = new Date(s.lastActiveAt).getTime();
    if (now.getTime() - ts < TWENTY_FOUR_HOURS_MS) return s;
  }
  return null;
};

export function ResumeBanner({ projectId, onResume }: ResumeBannerProps) {
  const t = useT();
  const rel = useRelativeTime();
  const { state } = useSessions({ projectId, limit: 5 });
  const [now] = useState(() => new Date());

  if (state.kind !== "ready") return null;
  const candidate = findResumable(state.sessions, now);
  if (!candidate) return null;

  return (
    <div
      data-testid="resume-banner"
      className="mb-3 flex items-center justify-between rounded-md border border-primary-line-soft bg-primary-soft px-3 py-2"
    >
      <div className="text-sm text-primary-fg-3">
        {t("common.continue")} <span className="font-medium">{candidate.title}</span>{" "}
        <span className="text-primary-fg">— {rel(candidate.lastActiveAt, now.getTime())}</span>
      </div>
      <Button onClick={() => onResume(candidate.sessionId)}>{t("projects.resume")}</Button>
    </div>
  );
}

// Used by tests to assert the inclusion rule without rendering.
export const _findResumable = findResumable;
